import { useIDEState } from '@/hooks/use-ide-state';
import { ProjectData } from '@/types';
import { ControlledMenu, MenuItem } from '@szhsin/react-menu';
import React, { useCallback, useState } from 'react';

interface ProjectContextMenuProps {
    project: ProjectData;
    children: React.ReactNode;
}

export const ProjectContextMenu: React.FC<ProjectContextMenuProps> = React.memo(({ project, children }) => {
    const { toggleProjectExpansion, addOutputMessage } = useIDEState();
    const [isOpen, setIsOpen] = useState(false);
    const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });

    const handleContextMenu = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        setAnchorPoint({ x: event.clientX, y: event.clientY });
        setIsOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleToggleExpansion = useCallback(() => {
        toggleProjectExpansion(project.id);
        setIsOpen(false);
    }, [toggleProjectExpansion, project.id]);

    const handleCopyPath = useCallback(async () => {
        const path = `/${project.name}`;
        const fullUrl = `${window.location.origin}${path}`;
        try {
            await navigator.clipboard.writeText(fullUrl);
            addOutputMessage(`Copied project path to clipboard: ${fullUrl}`, 'debug');
        } catch (error) {
            console.error('Failed to copy project path:', error);
            addOutputMessage(`Failed to copy project path: ${error}`, 'error');
        }
        setIsOpen(false);
    }, [project.name, addOutputMessage]);

    return (
        <div onContextMenu={handleContextMenu}>
            {children}
            <ControlledMenu anchorPoint={anchorPoint} state={isOpen ? 'open' : 'closed'} onClose={handleClose}>
                <MenuItem onClick={handleToggleExpansion} className='szh-menu__item'>
                    <span className='w-4 text-xs text-center mr-2'>{project.isExpanded ? '▼' : '▶'}</span>
                    {project.isExpanded ? 'Collapse' : 'Expand'}
                </MenuItem>
                <MenuItem onClick={handleCopyPath} className='szh-menu__item'>
                    <span className='w-4 text-xs text-center mr-2'>📋</span>
                    Copy Path
                </MenuItem>
            </ControlledMenu>
        </div>
    );
});
